import React, { useState, useEffect, useRef, useCallback, createContext, useContext } from 'react';
import { useParams, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { IonButton, IonIcon } from '@ionic/react';
import { peopleOutline, folderOpenOutline, arrowBackOutline } from 'ionicons/icons';
import { Helmet } from 'react-helmet-async';
import ProjectsService from '../../services/projects';
import InviteModal from '../../components/organisms/Modals/InviteModal/InviteModal';
import AnalyzeModal from '../../components/shared/AnalyzeModal';
import ImplementModal from '../../components/shared/ImplementModal';
import { IonToast } from '@ionic/react';
import PageLayout from '../../components/layouts/PageLayout';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import api, { accountApi } from '../../services/api';

// Context for sharing search state between ProjectLayout and TicketsPage
const TicketsSearchContext = createContext(null);
export const useTicketsSearch = () => useContext(TicketsSearchContext);

// Context for sharing tickets data and fetch function between components
const TicketsDataContext = createContext(null);
export const useTicketsData = () => useContext(TicketsDataContext);

// Context for sharing project data
const ProjectDataContext = createContext(null);
export const useProjectData = () => useContext(ProjectDataContext);

// Context for sharing selected tickets state
const SelectedTicketsContext = createContext(null);
export const useSelectedTickets = () => useContext(SelectedTicketsContext);

// Context for sharing issues search state between ProjectLayout and SentryIssuesPage
const IssuesSearchContext = createContext(null);
export const useIssuesSearch = () => useContext(IssuesSearchContext);

const ProjectLayout = () => {
  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const inviteButtonRef = useRef(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [analyzeModalOpen, setAnalyzeModalOpen] = useState(false);
  const [implementModalOpen, setImplementModalOpen] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', color: '' });
  const [executionName, setExecutionName] = useState(null);
  const [executionCollectionId, setExecutionCollectionId] = useState(null);
  const [collections, setCollections] = useState([]);
  const [executions, setExecutions] = useState([]);
  // Pipelines state — same pattern as tickets/collections so the breadcrumb
  // dropdown on project name can show the pipelines list when on a /pipelines
  // route. Lazy-loaded on dropdown hover (BreadcrumbDropdown.onFetch).
  const [pipelines, setPipelines] = useState([]);
  // Apps state — mirrors `pipelines` above. Powers the breadcrumb dropdown
  // on the project crumb when the user is on an /apps page, AND is used
  // (via .find) to resolve the third crumb's label on app-detail URLs
  // where the path only carries the instanceId, not the human name.
  const [apps, setApps] = useState([]);
  // Tickets state - shared via context with TicketsPage
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketsSyncing, setTicketsSyncing] = useState(false);
  const [ticketsError, setTicketsError] = useState(() => sessionStorage.getItem('jira_integration_error'));
  const [ticketsLastSynced, setTicketsLastSynced] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTickets, setSelectedTickets] = useState(new Set());
  const [issuesSearchQuery, setIssuesSearchQuery] = useState('');
  const [batchAnalyzeModalOpen, setBatchAnalyzeModalOpen] = useState(false);
  
  // Check if we're on the LEGACY top-level execution details route
  // (/projects/:id/runs/:exec — rendered outside pipelines). The newer
  // pipeline-scoped run URL (/projects/:id/pipelines/:type/runs/:exec)
  // is just a tab on the pipeline page; it should NOT take this branch
  // (which builds a different breadcrumb shape + expects executionName
  // to be populated by the legacy detail page — leaving an empty
  // breadcrumb segment when fired on the pipeline-scoped URL).
  const isExecutionRoute = /^\/projects\/[^/]+\/runs\//.test(location.pathname);
  const isTicketsPage = location.pathname.includes('/tickets');
  const isTicketDetailsPage = location.pathname.match(/\/tickets\/[^/]+$/); // Matches /tickets/:ticketKey
  const isTicketsListPage = isTicketsPage && !isTicketDetailsPage;
  const isIssuesPage = location.pathname.includes('/issues');
  const isIssueDetailsPage = location.pathname.match(/\/issues\/[^/]+$/); // Matches /issues/:issueId
  const isIssuesListPage = isIssuesPage && !isIssueDetailsPage;
  const isSettingsPage = location.pathname.includes('/settings');
  // Agents list / agent detail. Detect both so we can render a
  // plain-text breadcrumb (no Collections dropdown) — Collections aren't
  // meaningful on Agents pages. `agents` is the canonical URL segment;
  // the legacy `pipelines` aliases still resolve, so match both.
  const isPipelinesPage = location.pathname.includes('/agents') || location.pathname.includes('/pipelines');
  // Agent detail page = anything under /agents/<workflowType>/... (or the
  // legacy /pipelines/<type>). Used to only match the bare /<type> URL
  // (regex anchored at $), which dropped the workflow breadcrumb segment as
  // soon as the user opened an inline run (URL grows /runs/:execId at the end).
  const isPipelineDetailPage = location.pathname.match(/\/(agents|pipelines)\/[^/]+(\/.*)?$/);
  // Apps list / app detail. Same shape as the Pipelines block above —
  // mirroring lets the breadcrumb dropdown surface the project's apps
  // list instead of the (irrelevant) Collections fallback. The detail
  // regex matches /apps/<instanceId>[/anything] so deep tabs (?tab=logs)
  // and any future nested routes keep the third crumb populated.
  const isProjectAppsPage = location.pathname.match(/^\/projects\/[^/]+\/apps(\/|$)/);
  const appDetailMatch = location.pathname.match(/^\/projects\/[^/]+\/apps\/([^/?]+)(\/.*)?$/);
  const isAppDetailPage = !!appDetailMatch;
  const currentAppInstanceId = appDetailMatch ? appDetailMatch[1] : null;
  
  // Get collection from URL
  const searchParams = new URLSearchParams(location.search);
  const selectedCollectionId = searchParams.get('collection');

  const fetchProjectDetails = useCallback(async () => {
    try {
      const data = await ProjectsService.getProject(projectId);
      setProject(data.project);
    } catch (error) {
      console.error('Error fetching project:', error);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const fetchCollections = useCallback(async () => {
    try {
      const data = await accountApi.get(`/collections?projectId=${projectId}`);
      setCollections(data.collections || []);
    } catch (error) {
      console.error('Error fetching collections:', error);
    }
  }, [projectId]);

  const fetchPipelines = useCallback(async () => {
    try {
      const data = await api.get(`/projects/${projectId}/workflows`);
      setPipelines(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching pipelines:', error);
      setPipelines([]);
    }
  }, [projectId]);

  const fetchApps = useCallback(async () => {
    try {
      const data = await api.get(`/apps?projectId=${projectId}`);
      // Endpoint returns `{ apps: [...] }` (handlers/apps.js list branch).
      setApps(Array.isArray(data?.apps) ? data.apps : []);
    } catch (error) {
      console.error('Error fetching apps:', error);
      setApps([]);
    }
  }, [projectId]);

  // Eager-load the apps list on app-detail pages so the third breadcrumb
  // crumb can resolve <instanceId> → <display name> on first render.
  // (Pipelines doesn't need this because the URL carries workflowType
  // directly — apps' URL only carries the opaque instance UUID.)
  useEffect(() => {
    if (isAppDetailPage) fetchApps();
  }, [isAppDetailPage, fetchApps]);

  // Shared ticket loading function with localStorage caching - used by TicketsPage and dropdown
  const loadTickets = useCallback(async (forceRefresh = false, jiraSpace = null) => {
    const loadStart = Date.now();
    try {
      setTicketsError(null);
      sessionStorage.removeItem('jira_integration_error');
      
      // Get current project to determine the jiraSpace if not provided
      let spaceKey = jiraSpace;
      if (!spaceKey) {
        // Use already-loaded project data to avoid duplicate API call
        spaceKey = project?.jiraProjectKey || 'default';
        console.log('📍 Loading tickets for space:', spaceKey);
      }
      
      // Include jiraSpace in cache key so different spaces have separate caches
      const CACHE_KEY = `tickets_${projectId}_${spaceKey}`;
      const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
      
      console.log('🔑 Cache key:', CACHE_KEY);
      
      // Clear old cache format (without space key) if it exists
      const oldCacheKey = `tickets_${projectId}`;
      if (localStorage.getItem(oldCacheKey)) {
        console.log('🗑️ Clearing old cache format:', oldCacheKey);
        localStorage.removeItem(oldCacheKey);
      }
      
      // Try to load from cache first (show immediately for better UX)
      if (!forceRefresh) {
        try {
          const cached = localStorage.getItem(CACHE_KEY);
          if (cached) {
            const { tickets: cachedTickets, timestamp } = JSON.parse(cached);
            const age = Date.now() - timestamp;
            
            if (age < CACHE_TTL && cachedTickets.length > 0) {
              console.log(`✅ Using cached tickets (age: ${Math.round(age / 1000)}s) - ${cachedTickets.length} tickets for space ${spaceKey}`);
              setTickets(cachedTickets);
              setTicketsLastSynced(new Date(timestamp).toISOString());
              // Continue to fetch fresh data in background (don't return)
              setTicketsSyncing(true);
            }
          }
        } catch (cacheError) {
          console.warn('Cache read error:', cacheError);
        }
      }
      
      // Show loading indicator
      if (forceRefresh) {
        setTicketsSyncing(true);
      } else {
        setTicketsLoading(true);
      }

      // Fetch fresh data from backend
      const url = forceRefresh 
        ? `/projects/${projectId}/tickets?refresh=true`
        : `/projects/${projectId}/tickets`;
      const data = await api.get(url);

      const elapsed = Date.now() - loadStart;
      if (elapsed < 600) await new Promise(r => setTimeout(r, 600 - elapsed));
      const freshTickets = data.tickets || [];
      
      // Update state with fresh data
      setTickets(freshTickets);
      setTicketsLastSynced(data.lastSynced);
      
      // Save to cache
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({
          tickets: freshTickets,
          timestamp: Date.now()
        }));
        console.log(`💾 Cached ${freshTickets.length} tickets`);
      } catch (cacheError) {
        console.warn('Cache write error:', cacheError);
      }
      
    } catch (error) {
      const elapsed = Date.now() - loadStart;
      if (elapsed < 600) await new Promise(r => setTimeout(r, 600 - elapsed));
      console.error('Error loading tickets:', error);
      setTicketsError(error.message);
      if (error.message.includes('not connected') || error.message.includes('not active')) {
        sessionStorage.setItem('jira_integration_error', error.message);
      }
    } finally {
      setTicketsLoading(false);
      setTicketsSyncing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]); // project is used from closure, don't add to deps to avoid recreating callback

  // Reset state when switching projects
  useEffect(() => {
    setTickets([]);
    setTicketsError(null);
    setTicketsLoading(false);
    setTicketsSyncing(false);
    setSelectedTickets(new Set());
    setSearchQuery('');
    setIssuesSearchQuery('');
    fetchProjectDetails();
    fetchCollections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Load tickets when on ticket-related pages
  useEffect(() => {
    if (isTicketsPage && tickets.length === 0) {
      loadTickets();
    }
  }, [isTicketsPage, tickets.length, loadTickets]);

  useEffect(() => {
    // Clear execution data when route changes
    if (!isExecutionRoute) {
      setExecutionName(null);
      setExecutionCollectionId(null);
    }
  }, [isExecutionRoute]);

  const fetchExecutions = async (collectionId) => {
    try {
      let url = `/executions?projectId=${projectId}&limit=50`;
      if (collectionId) {
        url += `&collection=${collectionId}`;
      }
      
      const data = await api.get(url);
      setExecutions(data.executions || []);
    } catch (error) {
      console.error('Error fetching executions:', error);
      setExecutions([]);
    }
  };

  const handleInvite = async (emails) => {
    setInviteSubmitting(true);
    try {
      const data = await api.post(`/projects/${projectId}/invitations`, { emails });
      setInviteModalOpen(false);
      // Return the result so InviteModal shows the success toast via the shared
      // ToastContext (reliable app-wide), instead of this page's local IonToast.
      return data;
    } catch (error) {
      console.error('Error sending invitations:', error);
      // Re-throw so the modal can surface the error toast + stay open to retry.
      throw error;
    } finally {
      setInviteSubmitting(false);
    }
  };

  // While the project fetch is in flight we fall THROUGH to the single main
  // return below (with project=null + a skeleton breadcrumb), rather than
  // early-returning a separate `<PageLayout><Outlet/></PageLayout>` tree.
  //
  // Why: that early-return made the root element type differ from the loaded
  // branch (a bare PageLayout vs the Fragment + 5 context providers + PageLayout
  // wrapper at the bottom). When `loading` flipped true→false, React saw a
  // different root type, UNMOUNTED the whole subtree, and REMOUNTED the child
  // route — so the child page ran its data fetch twice and the content visibly
  // flashed (skeleton → rows → skeleton → rows). Keeping a single return path
  // pins the <Outlet> at a stable tree position, so the child mounts exactly
  // once and just re-renders as `project` flows null→loaded. Child pages already
  // guard the null project via `useOutletContext() || {}`.
  if (!loading && !project) {
    return (
      <PageLayout breadcrumbs={[{ label: 'Projects', path: '/projects' }, { label: 'Not Found' }]}>
        <NotFoundPage
          code="404"
          title="Project not found"
          subtitle="This project doesn't exist, or it may have been deleted. Check the URL or pick one from your project list."
          primaryAction={{ label: 'Back to projects', to: '/projects', icon: arrowBackOutline }}
          secondaryAction={{ label: 'Browse projects', to: '/projects', icon: folderOpenOutline }}
        />
      </PageLayout>
    );
  }

  // Handle collection filter by updating URL
  const handleCollectionFilter = (collectionId) => {
    if (collectionId) {
      navigate(`/projects/${projectId}/testruns?collection=${collectionId}`);
    } else {
      navigate(`/projects/${projectId}/testruns`);
    }
  };

  // Find selected collection name
  const selectedCollection = selectedCollectionId 
    ? collections.find(c => c.collectionId === selectedCollectionId)
    : null;
    
  // Find execution's collection name
  const executionCollection = executionCollectionId
    ? collections.find(c => c.collectionId === executionCollectionId)
    : null;

  // Build breadcrumbs based on route.
  // `loading` MUST be the first branch: every project-based branch below
  // dereferences `project.name` (unguarded), so they must not run until the
  // project has loaded. During load we show the same skeleton breadcrumb the
  // old early-return used.
  let breadcrumbs;
  if (loading) {
    breadcrumbs = [{ label: 'Projects', path: '/projects' }, { label: '', loading: true }];
  } else if (isExecutionRoute) {
    const baseBreadcrumbs = [
      { label: 'Projects', path: '/projects' },
      { label: project.name, path: `/projects/${projectId}/testruns`, projectId: projectId }
    ];
    
    // Add collection if execution has one - with executions dropdown
    if (executionCollection) {
      baseBreadcrumbs.push({
        label: executionCollection.collectionName || executionCollection.name,
        isCollectionWithExecutions: true,
        collectionId: executionCollectionId
      });
    }
    
    // Add execution name (plain text, no dropdown)
    baseBreadcrumbs.push(
      executionName ? { label: executionName } : { label: '', loading: true }
    );
    
    breadcrumbs = baseBreadcrumbs;
  } else if (isSettingsPage) {
    // On settings page - no dropdown, just plain text
    breadcrumbs = [
      { label: 'Projects', path: '/projects' },
      { label: project.name, path: `/projects/${projectId}/settings` }
    ];
  } else if (isTicketDetailsPage) {
    // On ticket details page - reuse same breadcrumb structure as tickets list
    const ticketKey = location.pathname.split('/tickets/')[1];
    breadcrumbs = [
      { label: 'Projects', path: '/projects' },
      { label: project.name, path: `/projects/${projectId}/tickets`, projectId: projectId, isTicketsPage: true },
      { label: ticketKey }
    ];
  } else if (isTicketsListPage) {
    // On tickets list page - show tickets dropdown
    breadcrumbs = [
      { label: 'Projects', path: '/projects' },
      { label: project.name, path: `/projects/${projectId}/tickets`, projectId: projectId, isTicketsPage: true }
    ];
  } else if (isIssueDetailsPage) {
    // On issue details page - link back to issues list
    const issueId = location.pathname.split('/issues/')[1];
    breadcrumbs = [
      { label: 'Projects', path: '/projects' },
      { label: project.name, path: `/projects/${projectId}/issues`, projectId: projectId },
      { label: issueId }
    ];
  } else if (isIssuesListPage) {
    // On issues list page
    breadcrumbs = [
      { label: 'Projects', path: '/projects' },
      { label: project.name, path: `/projects/${projectId}/issues`, projectId: projectId }
    ];
  } else if (isPipelineDetailPage) {
    // /projects/:id/pipelines/:type[/anything] — same convention as
    // ticket-details: project crumb gets `isPipelinesPage: true` so the
    // dropdown shows the pipelines list (BreadcrumbDropdown), terminal
    // crumb is the current pipeline name as plain text. Inline run
    // detail (URL = /pipelines/:type/runs/:exec) keeps the SAME
    // breadcrumb — the run is just a tab on the workflow page, not
    // a separate breadcrumb level.
    const workflowType = location.pathname.split(/\/(?:agents|pipelines)\//)[1].split('/')[0];
    breadcrumbs = [
      { label: 'Projects', path: '/projects' },
      { label: project.name, path: `/projects/${projectId}/agents`, projectId: projectId, isPipelinesPage: true },
      { label: workflowType }
    ];
  } else if (isPipelinesPage) {
    // /projects/:id/pipelines — list page. Same shape as TicketsListPage:
    // project crumb has `isPipelinesPage: true` to trigger the pipelines
    // dropdown.
    breadcrumbs = [
      { label: 'Projects', path: '/projects' },
      { label: project.name, path: `/projects/${projectId}/agents`, projectId: projectId, isPipelinesPage: true }
    ];
  } else if (isAppDetailPage) {
    // /projects/:id/apps/:instanceId[/anything] — same pattern as
    // PipelineDetail above. The project crumb is flagged isAppsPage so
    // PageLayout renders the Apps dropdown (NOT the Collections one).
    // Third crumb: prefer the app's display name (resolved from the
    // already-fetched apps list), fall back to its appType, fall back to
    // the first 8 chars of the instance UUID so the crumb is never
    // empty on first paint.
    const currentApp = apps.find((a) => a.instanceId === currentAppInstanceId);
    const appCrumbLabel =
      currentApp?.name
      || currentApp?.appType
      || (currentAppInstanceId ? currentAppInstanceId.slice(0, 8) : 'App');
    breadcrumbs = [
      { label: 'Projects', path: '/projects' },
      { label: project.name, path: `/projects/${projectId}/apps`, projectId: projectId, isAppsPage: true },
      { label: appCrumbLabel }
    ];
  } else if (isProjectAppsPage) {
    // /projects/:id/apps — list page. Project crumb is flagged isAppsPage
    // so PageLayout's breadcrumb dropdown surfaces the apps list (not
    // the irrelevant Collections fallback).
    breadcrumbs = [
      { label: 'Projects', path: '/projects' },
      { label: project.name, path: `/projects/${projectId}/apps`, projectId: projectId, isAppsPage: true }
    ];
  } else if (selectedCollection) {
    // On test runs page with collection selected
    breadcrumbs = [
      { label: 'Projects', path: '/projects' },
      { label: project.name, path: `/projects/${projectId}/testruns`, projectId: projectId },
      { label: selectedCollection.collectionName || selectedCollection.name, isCollection: true }
    ];
  } else {
    // On test runs page - show collections dropdown
    breadcrumbs = [
      { label: 'Projects', path: '/projects' },
      { label: project.name, path: `/projects/${projectId}/testruns`, projectId: projectId }
    ];
  }

  const headerLeft = null;

  // Handle ticket implementation
  const handleImplementTicket = async (name, additionalContext, model) => {
    const ticketKey = location.pathname.split('/tickets/')[1];
    
    try {
      console.log(`🚀 Implementing ticket: ${ticketKey} with name: ${name}`);
      
      const data = await api.post(`/projects/${projectId}/tickets/${ticketKey}/implement`, {
        name,
        additionalContext,
        model,
        agentType: project?.aiAgent || 'claude',
      });
      
      console.log(`✅ Implementation started:`, data);
      
      setToast({
        show: true,
        message: `Implementation "${name}" started! Execution ID: ${data.executionId}`,
        color: 'success'
      });
      
      // Close modal and navigate to implementation details page
      setImplementModalOpen(false);
      if (data.executionId) {
        setTimeout(() => {
          navigate(`/projects/${projectId}/implementations/${data.executionId}`);
        }, 1500);
      }
      
    } catch (error) {
      console.error('❌ Failed to implement ticket:', error);
      setToast({
        show: true,
        message: error.message || 'Failed to start implementation',
        color: 'danger'
      });
      throw error; // Re-throw so modal can handle it
    }
  };

  // Handle ticket analysis
  const handleAnalyzeTicket = async (name, additionalContext, model) => {
    const ticketKey = location.pathname.split('/tickets/')[1];
    
    try {
      console.log(`🔍 Analyzing ticket: ${ticketKey} with name: ${name}`);
      
      const data = await api.post(`/projects/${projectId}/tickets/${ticketKey}/analyze`, {
        name,
        additionalContext,
        model,
        agentType: project?.aiAgent || 'claude',
      });
      
      console.log(`✅ Analysis started:`, data);
      
      // Close modal and navigate to analysis details page first
      setAnalyzeModalOpen(false);
      if (data.executionId) {
        navigate(`/projects/${projectId}/analysis/${data.executionId}`);
        
        // Show toast after navigation
        setTimeout(() => {
          setToast({
            show: true,
            message: `Analysis "${name}" started!`,
            color: 'success'
          });
        }, 100);
      }
      
    } catch (error) {
      console.error('❌ Failed to analyze ticket:', error);
      setToast({
        show: true,
        message: error.message || 'Failed to start analysis',
        color: 'danger'
      });
      throw error; // Re-throw so modal can handle it
    }
  };

  // Batch analyze handler
  const handleBatchAnalyze = async (name, additionalContext, model) => {
    const ticketKeys = Array.from(selectedTickets);
    
    try {
      console.log(`🔍 Analyzing ${ticketKeys.length} tickets:`, ticketKeys);
      
      const results = [];
      for (const ticketKey of ticketKeys) {
        try {
          const data = await api.post(`/projects/${projectId}/tickets/${ticketKey}/analyze`, {
            name: `${name} - ${ticketKey}`,
            additionalContext,
            model,
            agentType: project?.aiAgent || 'claude',
          });
          results.push({ ticketKey, success: true, executionId: data.executionId });
        } catch (err) {
          console.error(`Failed to analyze ${ticketKey}:`, err);
          results.push({ ticketKey, success: false, error: err.message });
        }
      }
      
      const successCount = results.filter(r => r.success).length;
      const failCount = results.length - successCount;
      
      setToast({
        show: true,
        message: `Batch analysis started: ${successCount} succeeded${failCount > 0 ? `, ${failCount} failed` : ''}`,
        color: successCount > 0 ? 'success' : 'danger'
      });
      
      setBatchAnalyzeModalOpen(false);
      setSelectedTickets(new Set());
            
    } catch (error) {
      console.error('❌ Failed to start batch analysis:', error);
      setToast({
        show: true,
        message: error.message || 'Failed to start batch analysis',
        color: 'danger'
      });
      throw error;
    }
  };

  const actions = null;

  // Invite button - always shown (portaled to header when in project)
  const inviteAction = !isExecutionRoute ? (
    <IonButton
      ref={inviteButtonRef}
      className="grey-button header-action-btn"
      onClick={() => setInviteModalOpen(true)}
    >
      <IonIcon icon={peopleOutline} slot="start" />
      Invite
    </IonButton>
  ) : null;

  return (
    <>
      <Helmet>
        <title>{project?.name ? `${project.name} - Zibby` : 'Project - Zibby'}</title>
        <meta name="description" content={project?.name ? `Manage ${project.name} project in Zibby` : 'Manage your project in Zibby'} />
      </Helmet>
      <ProjectDataContext.Provider value={{ project }}>
      <TicketsDataContext.Provider value={{
        tickets,
        setTickets,
        loadTickets,
        ticketsLoading,
        ticketsSyncing,
        ticketsError,
        ticketsLastSynced
      }}>
        <SelectedTicketsContext.Provider value={{ selectedTickets, setSelectedTickets }}>
          <TicketsSearchContext.Provider value={{ searchQuery, setSearchQuery }}>
          <IssuesSearchContext.Provider value={{ issuesSearchQuery, setIssuesSearchQuery }}>
          <PageLayout
            breadcrumbs={breadcrumbs}
            actions={actions}
            headerLeft={headerLeft}
            inviteAction={inviteAction}
            onCollectionSelect={handleCollectionFilter}
            executions={executions}
            onFetchExecutions={fetchExecutions}
            projectId={projectId}
            tickets={tickets}
            pipelines={pipelines}
            onFetchPipelines={fetchPipelines}
            apps={apps}
            onFetchApps={fetchApps}
          >
            <Outlet context={{ project, setToast, refreshProject: fetchProjectDetails, setExecutionName, setExecutionCollectionId, setBatchAnalyzeModalOpen, setAnalyzeModalOpen }} />

      <InviteModal
        isOpen={inviteModalOpen}
        onDismiss={() => setInviteModalOpen(false)}
        onSubmit={handleInvite}
        isSubmitting={inviteSubmitting}
        projectName={project?.name || ''}
        projectId={projectId}
        triggerRef={inviteButtonRef}
      />

      <AnalyzeModal
        isOpen={analyzeModalOpen}
        onClose={() => setAnalyzeModalOpen(false)}
        onAnalyze={handleAnalyzeTicket}
        ticketKey={isTicketDetailsPage ? location.pathname.split('/tickets/')[1] : isIssueDetailsPage ? location.pathname.split('/issues/')[1] : ''}
        existingNames={executions.filter(e => e.type === 'analysis').map(e => e.name).filter(Boolean)}
        project={project}
      />

      <ImplementModal
        isOpen={implementModalOpen}
        onClose={() => setImplementModalOpen(false)}
        onImplement={handleImplementTicket}
        ticketKey={isTicketDetailsPage ? location.pathname.split('/tickets/')[1] : ''}
        existingNames={executions.filter(e => e.type === 'implementation').map(e => e.name).filter(Boolean)}
        project={project}
      />

      <AnalyzeModal
        isOpen={batchAnalyzeModalOpen}
        onClose={() => setBatchAnalyzeModalOpen(false)}
        onAnalyze={handleBatchAnalyze}
        ticketKey={selectedTickets.size > 1 ? `${selectedTickets.size} ${isIssuesPage ? 'issues' : 'tickets'}` : Array.from(selectedTickets)[0]}
        existingNames={executions.filter(e => e.type === 'analysis').map(e => e.name).filter(Boolean)}
        project={project}
      />

      <IonToast
        isOpen={toast.show}
        message={toast.message}
        duration={3000}
        onDidDismiss={() => setToast({ show: false, message: '', color: '' })}
        cssClass={`custom-toast custom-toast--${toast.color || 'info'}`}
      />
          </PageLayout>
          </IssuesSearchContext.Provider>
          </TicketsSearchContext.Provider>
        </SelectedTicketsContext.Provider>
      </TicketsDataContext.Provider>
      </ProjectDataContext.Provider>
    </>
  );
};

export default ProjectLayout;

